import React from 'react';
import { Truck, Package, Warehouse, CheckCircle } from 'lucide-react';
import { useCMS } from '../../context/CMSContext';

const ICON_MAP = { 'Full Truck Load': Truck, 'Part Load': Package, 'Warehousing': Warehouse, 'Express Surface Transport': Truck, 'Retail Logistics': Package, 'E-commerce Fulfillment': Package, 'Distribution Logistics': Truck, 'Packers & Movers': Package, 'Vehicle Rental': Truck, 'Chemical Logistics': Warehouse, 'Fragile Goods': Package };
const getIcon = (title) => Object.entries(ICON_MAP).find(([k]) => title?.includes(k))?.[1] || Package;

const DEFAULT_SERVICE_IMAGES = [
  'https://images.unsplash.com/photo-1605705658744-45f0fe8f9663?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1766561994067-dbd575e1cff2?crop=entropy&cs=srgb&fm=jpg&q=85',
  'https://images.unsplash.com/photo-1768796373360-95d80c5830fb?crop=entropy&cs=srgb&fm=jpg&q=85'
];

const ServiceCard = ({ image, alt, icon: Icon, title, description, features, testId }) => (
  <div className="group relative bg-white dark:bg-slate-900/50 rounded-3xl overflow-hidden border border-slate-200 dark:border-white/5 hover:border-amber-500 hover:shadow-2xl transition-all duration-500" data-testid={testId}>
    <div className="aspect-[4/3] overflow-hidden">
      <img src={image} alt={alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
    </div>
    <div className="p-8">
      <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform"><Icon className="w-8 h-8 text-amber-500" /></div>
      <h3 className="font-outfit text-2xl font-bold text-slate-900 dark:text-white mb-4">{title}</h3>
      <p className="text-slate-700 dark:text-slate-400 leading-relaxed mb-6">{description}</p>
      <ul className="space-y-3">
        {(features || []).map(f => <li key={f} className="flex items-center gap-3 text-slate-800 dark:text-slate-300"><CheckCircle className="w-5 h-5 text-amber-500 flex-shrink-0" /><span>{f}</span></li>)}
      </ul>
    </div>
  </div>
);

export const ServicesSection = () => {
  const cms = useCMS();
  const services = cms?.services || [];
  const extended = cms?.extended_services || [];

  return (
    <>
      <section id="services" className="py-32 bg-white dark:bg-[#0B0F19] relative overflow-hidden" data-testid="services-section">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50 to-white dark:from-transparent dark:to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-600 dark:text-amber-500 rounded-full text-sm font-bold mb-4">OUR SERVICES</span>
            <h2 className="font-outfit text-5xl font-black text-slate-900 dark:text-white mb-6">Complete Logistics Solutions</h2>
            <p className="text-xl text-slate-700 dark:text-slate-400 max-w-3xl mx-auto">From full truckload to part load and warehousing - we've got all your logistics needs covered</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((s, i) => <ServiceCard key={s.title || i} image={s.image_url || DEFAULT_SERVICE_IMAGES[i] || DEFAULT_SERVICE_IMAGES[0]} alt={s.title} icon={getIcon(s.title)} title={s.title} description={s.description} features={s.features} testId={`service-${i}`} />)}
          </div>
        </div>
      </section>
      <section className="py-24 bg-slate-50 dark:bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-amber-500/10 text-amber-600 dark:text-amber-500 rounded-full text-sm font-bold mb-4">MORE SERVICES</span>
            <h2 className="font-outfit text-4xl font-black text-slate-900 dark:text-white mb-4">Specialized Logistics Solutions</h2>
            <p className="text-lg text-slate-700 dark:text-slate-400 max-w-2xl mx-auto">Tailored services for every industry and requirement</p>
          </div>
          <div className="grid md:grid-cols-4 gap-6">
            {extended.map((svc, i) => {
              const Icon = getIcon(svc.title);
              return (
                <div key={svc.title || i} className="bg-white dark:bg-slate-900/50 rounded-xl p-6 border border-slate-200 dark:border-white/5 hover:border-amber-500 hover:shadow-lg transition-all duration-300 group">
                  <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"><Icon className="w-6 h-6 text-amber-500" /></div>
                  <h3 className="font-outfit text-lg font-bold text-slate-900 dark:text-white mb-2">{svc.title}</h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">{svc.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
